import {
  Alert,
  Button,
  Card,
  Descriptions,
  Drawer,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
} from "antd"
import { useEffect, useRef, useState } from "react"
import { v4 as uuidv4 } from "uuid"
import { siteWorkflowApi } from "../../utils/siteWorkflowApi"
import { actionStates, actionTransitions, isOverdue, statusLabels } from "../../types/siteWorkflows"
import type {
  ActionStatus,
  CorrectiveAction,
  CorrectiveActionWrite,
  AnswerFollowUp,
  InspectionFinding,
} from "../../types/siteWorkflows"
import { useWorkflowResource } from "./useWorkflowResource"
import {
  AssigneePicker,
  NoWorkflowItems,
  TargetPicker,
  WorkflowError,
  WorkflowHistory,
  WorkflowPager,
} from "./WorkflowCommon"
import { useWorkflowSession } from "./workflowSession"

export function FindingSummary({ finding }: { finding: InspectionFinding }) {
  const label = String(finding.definition.label ?? finding.definition.text ?? finding.pointUuid)
  return (
    <Space direction="vertical" size={4} style={{ width: "100%" }}>
      <strong>{label}</strong>
      {["reference", "method", "expected"].map((key) =>
        finding.definition[key] ? (
          <div key={key}>
            <Typography.Text type="secondary">
              {key === "reference" ? "Référence" : key === "method" ? "Méthode" : "Critère attendu"}{" "}
              :{" "}
            </Typography.Text>
            {String(finding.definition[key])}
          </div>
        ) : null,
      )}
      <span>{[finding.equipmentName, finding.contextLabel].filter(Boolean).join(" · ")}</span>
      <Tag>{finding.status == null ? "Sans réponse" : statusLabels[finding.status]}</Tag>
      {finding.comment && <div style={{ whiteSpace: "pre-wrap" }}>{finding.comment}</div>}
      {finding.measurement && <div>Mesure : {finding.measurement}</div>}
      {finding.severity && <div>Gravité : {finding.severity}</div>}
      {finding.recommendation && <div>Préconisation : {finding.recommendation}</div>}
      {finding.immediateAction && <div>Mesure immédiate : {finding.immediateAction}</div>}
      {finding.dueDate && <div>Échéance constatée : {finding.dueDate}</div>}
      {finding.photoException && <div>Exception photo : {finding.photoException}</div>}
      {finding.pictures.length > 0 && (
        <Space wrap>
          {finding.pictures.map((id) => (
            <a key={id} href={`/api/uploads/${id}`} target="_blank" rel="noreferrer">
              Photo du constat
            </a>
          ))}
        </Space>
      )}
    </Space>
  )
}
export function ActionEditor({
  site,
  enterprise,
  action,
  source,
  initialAsset,
  initialComponent,
  onCancel,
  onSaved,
}: {
  initialAsset?: string
  initialComponent?: string
  site: string
  enterprise: string
  action?: CorrectiveAction
  source?: AnswerFollowUp
  onCancel: () => void
  onSaved: (v: CorrectiveAction) => void
}) {
  const [form] = Form.useForm()
  const requestId = useRef(action?.uuid ?? uuidv4())
  const { canVerify } = useWorkflowSession()
  const [asset, setAsset] = useState(
    action?.assetUuid ?? source?.finding.assetUuid ?? initialAsset ?? null,
  )
  const [component, setComponent] = useState(
    action?.componentUuid ?? source?.finding.componentUuid ?? initialComponent ?? null,
  )
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>()
  const [checking, setChecking] = useState(false)
  const save = async () => {
    const fields = await form.validateFields().catch(() => null)
    if (!fields) return
    setBusy(true)
    setError(undefined)
    try {
      const body: CorrectiveActionWrite = {
        uuid: requestId.current,
        expectedVersion: action?.version,
        assetUuid: asset,
        componentUuid: component,
        ...fields,
        assigneeUuid: fields.assigneeUuid || null,
        dueDate: fields.dueDate || null,
        sourceAnswerUuid: source?.finding.answerUuid ?? undefined,
      }
      const value = await siteWorkflowApi.saveAction(site, body, action?.uuid)
      onSaved(value)
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }
  const verifySaved = async () => {
    setChecking(true)
    try {
      const value = await siteWorkflowApi.action(requestId.current)
      onSaved(value.action)
    } catch (e) {
      setError(e)
    } finally {
      setChecking(false)
    }
  }
  return (
    <Modal
      open
      title={action ? "Modifier l’action corrective" : "Nouvelle action corrective"}
      onCancel={onCancel}
      onOk={save}
      confirmLoading={busy}
      okText="Enregistrer"
      cancelText="Fermer"
      maskClosable={false}
      closable={!busy}
      cancelButtonProps={{ disabled: busy }}
    >
      <WorkflowError error={error} />
      {!!error && !action && (
        <Button loading={checking} onClick={verifySaved}>
          Vérifier si l’action a déjà été enregistrée
        </Button>
      )}
      {source && (
        <Alert
          type="info"
          message="Le constat d’origine sera conservé. La clôture nécessite une vérification explicite par un administrateur."
        />
      )}
      <Form
        form={form}
        layout="vertical"
        disabled={busy}
        initialValues={{
          title:
            action?.title ??
            String(source?.finding.definition.label ?? "Action corrective").slice(0, 250),
          description: action?.description ?? source?.finding.recommendation ?? "",
          priority: action?.priority ?? (source?.finding.severity || "major"),
          status: action?.status ?? "open",
          dueDate: action?.dueDate ?? source?.finding.dueDate ?? "",
          assigneeUuid: action?.assigneeUuid,
          reason: action ? "" : "Action créée depuis le dossier du site",
        }}
      >
        <Form.Item
          name="title"
          label="Titre"
          rules={[{ required: true, whitespace: true }, { max: 250 }]}
        >
          <Input maxLength={250} />
        </Form.Item>
        <Form.Item label="Équipement / composant concerné">
          <TargetPicker
            site={site}
            asset={asset}
            component={component}
            onAsset={setAsset}
            onComponent={setComponent}
            disabled={!!action || !!source}
          />
        </Form.Item>
        <Form.Item name="description" label="Travaux / action à réaliser" rules={[{ max: 6000 }]}>
          <Input.TextArea rows={3} maxLength={6000} />
        </Form.Item>
        <Form.Item name="priority" label="Priorité" rules={[{ required: true }]}>
          <Select
            options={[
              { value: "minor", label: "Mineure" },
              { value: "major", label: "Majeure" },
              { value: "critical", label: "Critique" },
            ]}
          />
        </Form.Item>
        <Form.Item name="assigneeUuid" label="Responsable">
          <AssigneePicker enterprise={enterprise} />
        </Form.Item>
        <Form.Item name="dueDate" label="Échéance de l’action">
          <Input type="date" min="2000-01-01" max="2100-12-31" />
        </Form.Item>
        {action && (
          <Form.Item name="status" label="État">
            <Select
              options={actionTransitions(action.status)
                .filter((s) => s !== "closed" || canVerify || action.status === "closed")
                .map((s) => ({ value: s, label: actionStates[s] }))}
            />
          </Form.Item>
        )}
        <Form.Item
          name="reason"
          label="Commentaire / motif de la modification"
          rules={[{ required: true, whitespace: true }, { max: 2000 }]}
        >
          <Input.TextArea rows={2} maxLength={2000} />
        </Form.Item>
      </Form>
    </Modal>
  )
}
function ActionObservationPicker({
  action,
  busy,
  onSave,
}: {
  action: CorrectiveAction
  busy: boolean
  onSave: (answer: string, reason: string) => void
}) {
  const [inspection, setInspection] = useState("")
  const [answer, setAnswer] = useState("")
  const [reason, setReason] = useState("")
  const [page, setPage] = useState(1)
  const [choices, setChoices] = useState<{ value: string; label: string }[]>([])
  const history = useWorkflowResource(
    `observation-inspections/${action.physicalSiteUuid}/${page}`,
    (signal) => siteWorkflowApi.inspections(action.physicalSiteUuid, page, signal),
  )
  useEffect(() => {
    if (history.data)
      setChoices((current) => {
        const values = new Map(current.map((c) => [c.value, c]))
        history.data!.items.forEach((i) =>
          values.set(i.uuid, {
            value: i.uuid,
            label: `${i.executionDate?.slice(0, 10) ?? "Sans date"} · ${i.missionName || i.siteName}`,
          }),
        )
        return Array.from(values.values())
      })
  }, [history.data])
  const source = useWorkflowResource(
    inspection ? `observation-findings/${inspection}` : "",
    (signal) => siteWorkflowApi.findings(inspection, signal),
  )
  const eligible = (source.data?.findings ?? []).filter(
    (f) =>
      f.answerUuid &&
      f.assetUuid === action.assetUuid &&
      f.componentUuid === action.componentUuid &&
      (!f.equipmentUuid || !!f.assetUuid),
  )
  const finding = eligible.find((f) => f.answerUuid === answer)
  return (
    <Card size="small" title="Rattacher un constat d’inspection">
      <Space direction="vertical" style={{ width: "100%" }}>
        <Select
          style={{ width: "100%" }}
          disabled={busy}
          aria-label="Inspection du constat"
          placeholder="Choisir l’inspection"
          showSearch
          optionFilterProp="label"
          value={inspection || undefined}
          options={choices}
          onChange={(v) => {
            setInspection(v)
            setAnswer("")
          }}
        />
        {page * 50 < (history.data?.total ?? 0) && (
          <Button onClick={() => setPage((n) => n + 1)}>Charger d’autres inspections</Button>
        )}
        <WorkflowError error={history.error || source.error} />
        {inspection && (
          <Select
            style={{ width: "100%" }}
            disabled={busy}
            aria-label="Constat à rattacher"
            placeholder="Choisir le constat du même équipement / composant"
            value={answer || undefined}
            onChange={setAnswer}
            showSearch
            optionFilterProp="label"
            loading={source.loading}
            options={eligible.map((f) => ({
              value: f.answerUuid!,
              label: `${String(f.definition.label ?? f.pointUuid)} · ${f.status == null ? "Sans réponse" : statusLabels[f.status]}`,
            }))}
          />
        )}
        {finding && <FindingSummary finding={finding} />}
        <Input.TextArea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={2000}
          placeholder="Motif du rattachement"
          disabled={busy}
        />
        <Button disabled={!answer || !reason.trim() || busy} onClick={() => onSave(answer, reason)}>
          Rattacher après vérification
        </Button>
      </Space>
    </Card>
  )
}
export function ActionDrawer({
  id,
  site,
  enterprise,
  onClose,
  onChanged,
}: {
  id: string
  site: string
  enterprise: string
  onClose: () => void
  onChanged?: () => void
}) {
  const data = useWorkflowResource(`action/${id}`, (signal) => siteWorkflowApi.action(id, signal))
  const [editing, setEditing] = useState(false),
    [comment, setComment] = useState("")
  const [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(),
    [refresh, setRefresh] = useState(0)
  const changed = () => {
    data.reload()
    setRefresh((n) => n + 1)
    onChanged?.()
  }
  const run = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setError(undefined)
    try {
      await work()
      setComment("")
      changed()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }
  const v = data.data?.action
  return (
    <Drawer
      open
      title={v?.title ?? "Action corrective"}
      width={720}
      onClose={onClose}
      closable={!busy}
      maskClosable={!busy}
    >
      <WorkflowError error={data.error} retry={data.reload} />
      <WorkflowError error={error} />
      {v && (
        <Space direction="vertical" style={{ width: "100%" }} size="middle">
          <Space wrap>
            <Tag color={v.status === "closed" ? "green" : "blue"}>{actionStates[v.status]}</Tag>
            <Tag>{v.priority}</Tag>
            {isOverdue(v.dueDate, v.status) && <Tag color="red">En retard</Tag>}
            <Button onClick={() => setEditing(true)}>Modifier / changer l’état</Button>
          </Space>
          <Descriptions
            column={1}
            size="small"
            items={[
              { key: "due", label: "Échéance", children: v.dueDate || "Non définie" },
              { key: "owner", label: "Responsable", children: v.assigneeUuid || "Non affectée" },
            ]}
          />
          <div style={{ whiteSpace: "pre-wrap" }}>{v.description}</div>
          <Typography.Title level={5}>Constats liés</Typography.Title>
          {data.data?.observations.map((o) => (
            <Card
              size="small"
              key={o.uuid}
              title={`Inspection · ${new Date(o.createdAt).toLocaleDateString("fr-FR")}`}
            >
              <FindingSummary finding={o.finding} />
              <Typography.Text type="secondary">Inspection : {o.inspectionUuid}</Typography.Text>
            </Card>
          ))}
          {!["closed", "cancelled"].includes(v.status) && (
            <ActionObservationPicker
              action={v}
              busy={busy}
              onSave={(sourceAnswer, reason) =>
                run(() => siteWorkflowApi.observe(v, sourceAnswer, reason))
              }
            />
          )}
          <Typography.Title level={5}>Pièces justificatives</Typography.Title>
          {data.data?.evidence.map((e) => (
            <a
              key={e.uuid}
              href={`/api/v2/corrective-actions/${id}/evidence/${e.uuid}`}
              target="_blank"
              rel="noreferrer"
            >
              {e.name}
            </a>
          ))}
          {!["closed", "cancelled"].includes(v.status) && (
            <Upload
              accept="image/jpeg,image/png,image/webp,application/pdf"
              showUploadList={false}
              disabled={busy}
              beforeUpload={(file) => {
                void run(() => siteWorkflowApi.evidence(id, file))
                return false
              }}
            >
              <Button loading={busy}>Ajouter une photo ou un PDF (8 Mio max.)</Button>
            </Upload>
          )}
          <Typography.Title level={5}>Historique et commentaires</Typography.Title>
          <Input.TextArea
            rows={2}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={2000}
            placeholder="Ajouter un commentaire"
          />
          <Button
            loading={busy}
            disabled={!comment.trim()}
            onClick={() => run(() => siteWorkflowApi.comment(v, comment))}
          >
            Ajouter le commentaire
          </Button>
          <WorkflowHistory site={site} kind="action" id={id} refresh={refresh} />
        </Space>
      )}
      {editing && v && (
        <ActionEditor
          site={site}
          enterprise={enterprise}
          action={v}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false)
            changed()
          }}
        />
      )}
    </Drawer>
  )
}
export function CorrectiveActionsPanel({
  site,
  enterprise,
  asset,
  component,
}: {
  site: string
  enterprise: string
  asset?: string
  component?: string
}) {
  const [page, setPage] = useState(1),
    [status, setStatus] = useState("")
  const [creating, setCreating] = useState(false),
    [selected, setSelected] = useState<string>()
  const data = useWorkflowResource(
    `actions/${site}/${status}/${page}/${asset ?? ""}/${component ?? ""}`,
    (signal) => siteWorkflowApi.actions(site, status, page, signal, asset, component),
  )
  const items = (data.data?.items ?? []).filter(
    (a) => (!asset || a.assetUuid === asset) && (!component || a.componentUuid === component),
  )
  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      <Space wrap>
        <Select
          aria-label="Filtrer les actions"
          value={status}
          style={{ minWidth: 180 }}
          onChange={(v) => {
            setStatus(v)
            setPage(1)
          }}
          options={[
            { value: "", label: "Tous les états" },
            { value: "overdue", label: "En retard" },
            ...Object.entries(actionStates).map(([value, label]) => ({ value, label })),
          ]}
        />
        <Button type="primary" onClick={() => setCreating(true)}>
          Nouvelle action
        </Button>
        <Button onClick={data.reload}>Actualiser</Button>
      </Space>
      <WorkflowError error={data.error} retry={data.reload} />
      {!data.loading && !items.length && <NoWorkflowItems />}
      {items.map((a) => (
        <Card
          key={a.uuid}
          size="small"
          title={
            <Button
              type="link"
              style={{ whiteSpace: "normal", height: "auto", padding: 0, textAlign: "left" }}
              onClick={() => setSelected(a.uuid)}
            >
              {a.title}
            </Button>
          }
        >
          <Space wrap>
            <Tag>{actionStates[a.status]}</Tag>
            <Tag>{a.priority}</Tag>
            {a.dueDate && <span>Échéance : {a.dueDate}</span>}
            {isOverdue(a.dueDate, a.status) && <Tag color="red">En retard</Tag>}
          </Space>
        </Card>
      ))}
      <WorkflowPager page={page} total={data.data?.total} onChange={setPage} />
      {creating && (
        <ActionEditor
          site={site}
          enterprise={enterprise}
          initialAsset={asset}
          initialComponent={component}
          onCancel={() => setCreating(false)}
          onSaved={(v) => {
            setCreating(false)
            setSelected(v.uuid)
            data.reload()
          }}
        />
      )}
      {selected && (
        <ActionDrawer
          key={selected}
          id={selected}
          site={site}
          enterprise={enterprise}
          onClose={() => setSelected(undefined)}
          onChanged={data.reload}
        />
      )}
    </Space>
  )
}
export function AnswerFollowUpButton({
  answerUuid,
  disabled,
}: {
  answerUuid: string
  disabled?: boolean
}) {
  const [open, setOpen] = useState(false),
    [creating, setCreating] = useState(false),
    [selected, setSelected] = useState<string>()
  const data = useWorkflowResource(open ? `follow-up/${answerUuid}` : "", (signal) =>
    siteWorkflowApi.followUp(answerUuid, signal),
  )
  const v = data.data
  const unresolved =
    !!v &&
    !!v.finding.equipmentUuid &&
    (!v.finding.assetUuid || (v.finding.contextKind === "component" && !v.finding.componentUuid))
  return (
    <>
      <Button size="small" disabled={disabled} onClick={() => setOpen(true)}>
        Suivi des actions
      </Button>
      <Modal
        open={open}
        title="Suivi du constat"
        footer={<Button onClick={() => setOpen(false)}>Fermer</Button>}
        onCancel={() => setOpen(false)}
      >
        <WorkflowError error={data.error} retry={data.reload} />
        {v && (
          <Space direction="vertical" style={{ width: "100%" }}>
            <FindingSummary finding={v.finding} />
            {v.actions.map((a) => (
              <Button key={a.uuid} onClick={() => setSelected(a.uuid)}>
                {a.title} · {actionStates[a.status]}
              </Button>
            ))}
            {unresolved ? (
              <Alert
                type="warning"
                message="Rattachez d’abord cet équipement et son composant au dossier du site."
              />
            ) : (
              <Button onClick={() => setCreating(true)}>
                Créer une action à partir de ce constat
              </Button>
            )}
          </Space>
        )}
      </Modal>
      {creating && v && (
        <ActionEditor
          site={v.physicalSiteUuid}
          enterprise={v.enterpriseUuid}
          source={v}
          onCancel={() => setCreating(false)}
          onSaved={(a) => {
            setCreating(false)
            setSelected(a.uuid)
            data.reload()
          }}
        />
      )}
      {selected && v && (
        <ActionDrawer
          key={selected}
          id={selected}
          site={v.physicalSiteUuid}
          enterprise={v.enterpriseUuid}
          onClose={() => setSelected(undefined)}
          onChanged={data.reload}
        />
      )}
    </>
  )
}
